/**
 * CEO Command Center - Application Entry Point
 *
 * @module main
 * @version 11.0.0
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './styles/index.css';

// Auto-initialize database on app start
// This ensures the database is ready before components check connection status
const initializeDatabase = async () => {
  try {
    // Dynamic import to avoid circular dependencies
    const { useSyncStore } = await import('./stores/syncStore');
    const syncStore = useSyncStore.getState();

    if (!syncStore.initialized && !syncStore.initializing) {
      console.log('[Main] Auto-initializing database...');
      await syncStore.init();
      console.log('[Main] Database initialized successfully');

      // Seed cash flow data after database is ready
      await seedCashFlowIfEmpty();
    }
  } catch (error) {
    console.warn('[Main] Database auto-init failed (will show setup prompt):', error);
    // Don't throw - let the app load and show the setup prompt
  }
};

// Data version - increment to force re-seed
const CASHFLOW_DATA_VERSION = 3;

// Seed cash flow data - check if data needs correction
const seedCashFlowIfEmpty = async () => {
  try {
    const { databaseService } = await import('./services/sync');
    const { useDataStoreSQLite } = await import('./stores/dataStoreSQLite');
    const { useSyncStore } = await import('./stores/syncStore');
    const { seedCashFlowData } = await import('./utils/cashflowImport');

    const spaceId = useSyncStore.getState().activeSpaceId || 'default';

    // Check data version in localStorage
    const storedVersion = localStorage.getItem('cashflow_data_version');
    const needsVersionUpdate = storedVersion !== String(CASHFLOW_DATA_VERSION);

    if (needsVersionUpdate) {
      console.log(`[Main] Cash flow data version changed (${storedVersion} → ${CASHFLOW_DATA_VERSION}), forcing re-seed...`);
      // Delete all cash flow data to re-seed with correct values
      await databaseService.run('DELETE FROM cashflow WHERE space_id = ?', [spaceId]);
      localStorage.setItem('cashflow_data_version', String(CASHFLOW_DATA_VERSION));
    }

    // Check database directly for existing cash flow data
    const existingData = await databaseService.exec<{ count: number }>(
      'SELECT COUNT(*) as count FROM cashflow WHERE space_id = ?',
      [spaceId]
    );

    const count = existingData[0]?.count || 0;

    if (count === 0) {
      console.log('[Main] No cash flow data found in DB, seeding...');

      // Parse the seed data
      const parseResult = await seedCashFlowData();

      if (parseResult.success && parseResult.records.length > 0) {
        // Import into database
        const dataStore = useDataStoreSQLite.getState();
        const importResult = await dataStore.importCashFlow(parseResult.records);
        console.log(`[Main] Seeded ${importResult.imported} cash flow records`);

        // Force reload to update UI
        await dataStore.loadAll();
      }
    } else {
      console.log(`[Main] Cash flow data already exists (${count} records), skipping seed`);
    }
  } catch (error) {
    console.warn('[Main] Cash flow seeding failed:', error);
  }
};

// Start initialization immediately (don't block render)
initializeDatabase();

// Mount application
const root = document.getElementById('root');

if (root) {
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
} else {
  console.error('Root element not found');
}
